// Hash router with an auth/admin guard.

import { currentUser, isAdmin } from "./lib/firebase";

export interface Route {
  pattern: RegExp;
  params: string[];
  render: (params: Record<string, string>) => void | Promise<void>;
  auth?: boolean; // requires signed-in guest (default true)
  admin?: boolean; // requires admin claim
}

const routes: Route[] = [];
let notFound: () => void = () => {};

/** Register a route. Path supports :params, e.g. "/chapter/:id". */
export function route(
  path: string,
  render: Route["render"],
  opts: { auth?: boolean; admin?: boolean } = {},
): void {
  const params: string[] = [];
  const pattern = new RegExp(
    "^" +
      path.replace(/:[^/]+/g, (m) => {
        params.push(m.slice(1));
        return "([^/]+)";
      }) +
      "$",
  );
  routes.push({ pattern, params, render, auth: opts.auth ?? true, admin: opts.admin });
}

export function setNotFound(fn: () => void): void {
  notFound = fn;
}

export function go(path: string): void {
  if (location.hash.slice(1) === path) resolve();
  else location.hash = path;
}

async function resolve(): Promise<void> {
  const path = location.hash.slice(1) || "/";
  // Widen the layout for the CONTROL console only (see body.admin-view in CSS).
  document.body.classList.toggle("admin-view", path.startsWith("/admin"));
  for (const r of routes) {
    const m = r.pattern.exec(path);
    if (!m) continue;

    if (r.auth && !currentUser()) return void go("/activate");
    if (r.admin && !(await isAdmin())) return void go("/hq");

    const params: Record<string, string> = {};
    r.params.forEach((p, i) => (params[p] = decodeURIComponent(m[i + 1])));
    await r.render(params);
    window.scrollTo(0, 0);
    return;
  }
  notFound();
}

export function startRouter(): void {
  window.addEventListener("hashchange", resolve);
  resolve();
}
